import { FC, useMemo } from 'react';

import { GiftOrderSearchParams } from '@shared/api/giftOrder/giftOrder.hook';
import { GiftOrderPaymentStatus } from '@shared/api/giftOrder/giftOrder.interface';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';

import { OrderSearchResultsView } from './OrderSearchResultsView';

interface OrdererOrdersModalProps {
  /** 주문자 핸드폰번호 (포맷팅된 그대로 받아도 됨 — 내부에서 숫자만 추출) */
  phoneNumber: string;
  /** 강조할 주문 — 쿠폰 내역 검색에서 "이 쿠폰이 든 주문"으로 들어온 경우 */
  highlightOrderId?: string;
  /** 강조 이유 라벨 (예: 쿠폰 17DA5C-5) */
  highlightLabel?: string;
  onClose: () => void;
}

export const OrdererOrdersModal: FC<OrdererOrdersModalProps> = ({
  phoneNumber,
  highlightOrderId,
  highlightLabel,
  onClose,
}) => {
  const params: GiftOrderSearchParams = useMemo(() => {
    const digits = phoneNumber.replace(/[^0-9]/g, '');
    return { phoneNumber: digits };
  }, [phoneNumber]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative my-4 flex max-h-[92vh] w-full max-w-[1440px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        {/* 헤더 */}
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">주문자 관련 주문 목록</h2>
            <p className="mt-0.5 text-xs text-gray-500">
              핸드폰번호{' '}
              <span className="font-medium text-gray-700">
                {phoneNumberStringFormator(phoneNumber)}
              </span>{' '}
              로 검색된 결과입니다.
              {highlightOrderId && (
                <>
                  {' · '}
                  <span className="font-medium text-indigo-600">
                    {highlightLabel ? `쿠폰 ${highlightLabel} — ` : ''}이 쿠폰이 들어있는
                    주문을 강조했습니다.
                  </span>
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="닫기"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* 본문 - 스크롤 가능 */}
        <div className="min-h-0 flex-1 overflow-auto bg-gray-50 p-4">
          {/* 쿠폰에서 들어온 조회라 미결제 주문은 볼 이유가 없다 — 결제완료로 열고,
              강조 대상이 다른 상태(확정 등)면 결과 뷰가 그 상태로 알아서 맞춘다 */}
          <OrderSearchResultsView
            searchParams={params}
            hasSearched
            highlightOrderId={highlightOrderId}
            highlightLabel={highlightLabel}
            defaultPaymentStatusFilter={GiftOrderPaymentStatus.Paid}
            csMemoPreview={false}
          />
        </div>
      </div>
    </div>
  );
};
